import React from 'react';
import {
  useProductCategories,
  useProductManagement,
  useProductActions,
  useProductStats,
  useConfirm,
  useToast,
} from '../hooks';
import { ProductManagementLayout } from '../components/products';

const ProductManagement: React.FC = () => {
  // Custom hooks
  const {
    mainCategories,
    filteredSubCategories,
    selectedMainCategory,
    getMainCategoryName,
    getSubCategoryName,
    handleMainCategoryChange,
  } = useProductCategories();

  const {
    products,
    optionTypes,
    loading,
    error,
    searchTerm,
    isEditing,
    showAdd,
    editProduct,
    newProduct,
    handleAdd: originalHandleAdd,
    handleEdit,
    handleEditSave: originalHandleEditSave,
    handleDelete: originalHandleDelete,
    handleShowAdd,
    handleCloseAdd,
    handleCloseEdit,
    updateNewProduct,
    updateEditProduct,
    setSearchTerm,
    clearError,
  } = useProductManagement();

  const { confirm, ConfirmComponent } = useConfirm();
  const { showToast, ToastComponent } = useToast();

  // Enhanced handlers with confirmations and toasts
  const { handleAdd, handleEditSave, handleConfirmDelete } = useProductActions({
    newProduct,
    editProduct,
    originalHandleAdd,
    originalHandleEditSave,
    originalHandleDelete,
    confirm,
    showToast,
  });

  // Statistics calculations using the new hook
  const { totalProducts, averagePrice, productsWithOptions, uniqueCategories, hasValidPrices } =
    useProductStats(products);

  const handleRetry = () => {
    // If there's a fetch function in useProductManagement, use it
    // Otherwise, we could refresh the page or take other action
    window.location.reload();
  };

  return (
    <ProductManagementLayout
      // Data
      products={products}
      loading={loading}
      error={error}
      searchTerm={searchTerm}
      // Statistics
      totalProducts={totalProducts}
      averagePrice={averagePrice}
      productsWithOptions={productsWithOptions}
      uniqueCategories={uniqueCategories}
      hasValidPrices={hasValidPrices}
      // Modals
      showAdd={showAdd}
      isEditing={isEditing}
      newProduct={newProduct}
      editProduct={editProduct}
      // Categories
      mainCategories={mainCategories}
      filteredSubCategories={filteredSubCategories}
      selectedMainCategory={selectedMainCategory}
      getMainCategoryName={getMainCategoryName}
      getSubCategoryName={getSubCategoryName}
      // Option Types
      optionTypes={optionTypes}
      // Handlers
      onSearchChange={setSearchTerm}
      onShowAdd={handleShowAdd}
      onCloseAdd={handleCloseAdd}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onCloseEdit={handleCloseEdit}
      onEditSave={handleEditSave}
      onConfirmDelete={handleConfirmDelete}
      onUpdateNewProduct={updateNewProduct}
      onUpdateEditProduct={updateEditProduct}
      onMainCategoryChange={handleMainCategoryChange}
      onClearError={clearError}
      onRetry={handleRetry}
      // Components
      ConfirmComponent={ConfirmComponent}
      ToastComponent={ToastComponent}
    />
  );
};

export default ProductManagement;
